import { useEffect, useMemo } from "react";
import type { PreviewResponse, ScenarioCalendarBasis } from "@opus-dt/shared-types";
import { usePreviewScenario } from "@/features/simulations/hooks";
import type { RunKpis, SandboxPeriod } from "@/types/sim-sandbox.types";
import { machineMeasured, periodReady, toSimulationPeriod } from "@/types/sim-sandbox.types";
import { useSandboxDraft } from "./useSandboxDraft";
import { computePreview } from "../preview-engine";

export interface SandboxPreview {
  /** KPIs a exibir no baseline (servidor prevalece; cliente é o imediato/fallback). */
  baseline: RunKpis;
  /** Estágio-gargalo p/ realce no canvas. */
  bottleneckStageKey: string | null;
  /** Vazão estimada por estágio, exibida no próprio desenho da linha. */
  perStage: Array<{ stageKey: string; throughputUnH: number }>;
  /** true ⇒ KPIs vêm do servidor (MTBF/MTTR re-derivados na janela de calibração). */
  serverPresent: boolean;
  /** true enquanto o preview do servidor recalcula (troca de janela/período). */
  recalculating: boolean;
  /** % das máquinas ativas com parâmetros medidos (null quando não há máquinas ativas). */
  measuredShare: number | null;
  /** Erro do preview do servidor: a UI mostra estado de erro, NUNCA
   * apresenta premissa como se fosse dado válido. null quando ok. */
  serverError: unknown;
  /** Refaz o preview do servidor após erro. */
  retryServer: () => void;
  /** Horas CORRIDAS do período. `null` quando não há como saber (modos OP
   * antes da resposta do servidor, ou FREE sem as duas datas) — a tela mostra "—", nunca um
   * número assumido. */
  runningHours: number | null;
  /** Horas OPERACIONAIS (∩ calendário) vindas do server; null ⇒ 24/7 (sem calendário
   * ou ainda não resolvido). O rótulo do sandbox exibe "Nh corridas · Mh operacionais". */
  effectiveHours: number | null;
  /** Base de expediente da rodada, dita pelo SERVIDOR. `null` só enquanto ele
   * não respondeu: a tela cala em vez de chutar "contínuo", que é o rótulo mais caro de errar. */
  calendarBasis: ScenarioCalendarBasis | null;
}

/**
 * Horas corridas que o CLIENTE consegue AFIRMAR a partir do rascunho: só no modo FREE com as duas
 * datas. `null` significa "não sei" — nos modos OP a janela real só existe no servidor.
 */
function periodHours(period: SandboxPeriod): number | null {
  if (period.mode === "FREE" && period.periodStart && period.periodEnd) {
    const h =
      (new Date(period.periodEnd).getTime() - new Date(period.periodStart).getTime()) / 3.6e6;
    if (h > 0) return Math.min(h, 720);
  }
  return null;
}

/**
 * Horizonte do MOTOR do preview leve: quando a janela ainda não é conhecida, assume 24 h para ter
 * o que calcular até a rodada detalhada.
 *
 * Este 24 é PREMISSA DE CÁLCULO e nunca deve ser exibido. Exibido, ele é indistinguível
 * de uma linha 24/7 medida: um cenário com expediente próprio de 6 h/dia mostrava "24h" no chip e
 * dizia ao usuário o oposto do que a rodada devolvia. Por isso a tela lê `periodHours` (que sabe
 * dizer "não sei") e o motor lê esta função.
 */
function engineHorizonHours(period: SandboxPeriod): number {
  return periodHours(period) ?? 24;
}

function serverKpis(s: PreviewResponse): RunKpis {
  return {
    oee: s.oee,
    availability: s.availability,
    performance: s.performance,
    quality: s.quality,
    estimatedProduction: s.estimatedProduction,
    // Spec 079 — o nível EXIBIDO. O servidor já resolve a régua no preview (`resolveConfidence`
    // com a MESMA cascata do worker); ler `confidenceLevel` cru aqui jogava esse trabalho fora e
    // fazia o sandbox anunciar a procedência enquanto a rodada anuncia o histórico (SC-003).
    confidence: s.displayedConfidenceLevel ?? s.confidenceLevel,
    bottleneckNames: [],
  };
}

// Consolida o preview LEVE do baseline: computação client-side imediata
// (determinística, sem fila) + preview do servidor (re-deriva MTBF/MTTR na janela). O servidor
// prevalece quando disponível e o rascunho não está sujo; enquanto edita (dirty), vale o cliente,
// que reflete a edição ao vivo. Retorna também o estágio-gargalo (canvas) e o % de máquinas medidas.
export function useSandboxPreview(
  scenarioId: string | undefined,
  hasSnap: boolean
): SandboxPreview {
  const machines = useSandboxDraft((s) => s.machines);
  const stages = useSandboxDraft((s) => s.stages);
  const period = useSandboxDraft((s) => s.period);
  const dirty = useSandboxDraft((s) => s.dirty);

  const server = usePreviewScenario(
    scenarioId,
    toSimulationPeriod(period),
    hasSnap && periodReady(period) && !dirty
  );

  // Horas RECEBIDAS do server. `server.data` sobrevive aos drags de parâmetro
  // (keepPreviousData sob a mesma chave keyed por período) — só muda com período/calendário. As
  // CORRIDAS vêm do server (janela real inclusive nos modos OP que o cliente não conhece);
  // fallback ao placeholder do cliente enquanto o server ainda não respondeu. "Tem calendário" ⇔
  // effectiveHours < runningHours (ambos server-side, mesma janela); 24/7 ⇒ iguais.
  const serverRunning = server.data?.runningHours;
  const serverEffective = server.data?.effectiveHours;
  const running = serverRunning ?? engineHorizonHours(period);
  // O que a TELA pode afirmar: sem resposta do servidor e sem janela no rascunho, nada.
  const displayRunning = serverRunning ?? periodHours(period);
  const hasCalendarHours =
    serverEffective != null && serverRunning != null && serverEffective + 1e-6 < serverRunning;

  const client = useMemo(
    () =>
      computePreview({
        machines: Object.values(machines),
        stages,
        horizonHours: running,
        effectiveHours: hasCalendarHours ? serverEffective : undefined,
        // Mesmo fator de janela do server (previewConfidence); default 90 no submit.
        calibrationDays: period.calibrationDays ?? 90,
      }),
    [machines, stages, period, running, hasCalendarHours, serverEffective]
  );
  // Desabilitada com edições não salvas (dirty), mas o React Query retém o último `data`
  // (keepPreviousData) sob a mesma chave; o guard evita mostrar KPIs da estrutura SALVA enquanto
  // o usuário edita — prevalece o cliente até salvar.
  const serverData = dirty ? undefined : server.data;

  // Injeta a base MEDIDA por máquina (re-derivada na janela) no rascunho p/ o sheet
  // exibir o valor real como base do slider. Contador/volume viajam junto e
  // decidem o aviso estrutural×transitório. Display-only: não marca dirty nem entra no snapshot.
  useEffect(() => {
    const bases = serverData?.machines;
    if (!bases?.length) return;
    useSandboxDraft.getState().setMeasuredBases(
      Object.fromEntries(
        bases.map((b) => [
          b.key,
          {
            baseProfile: b.baseProfile,
            counterConfigured: b.productionCounterConfigured,
            producedInWindow: b.producedInWindow,
            // Par do produto ativo (o cliente aplica com o helper compartilhado).
            pairProfile: b.pairProfile,
          },
        ])
      )
    );
  }, [serverData]);

  // Máquina conta como "medida" quando QUALQUER parâmetro derivável está medido
  // (origem por parâmetro via originOf; escalar legado coberto pelo fallback). Mesmo critério do
  // badge do nó do canvas (machineMeasured), p/ não divergir.
  const present = Object.values(machines).filter((m) => m.present);
  const measured = present.filter(machineMeasured).length;

  return {
    baseline: serverData ? serverKpis(serverData) : client,
    bottleneckStageKey: serverData?.bottleneckStageKey ?? client.bottleneckStageKey ?? null,
    // A estimativa deixou de ser um bloco de resultado e virou retorno IN LOCO:
    // a vazão de cada estágio aparece no próprio desenho, onde a edição acontece. Enquanto o
    // rascunho está sujo vale o cálculo do cliente (reflete o slider ao vivo).
    perStage: serverData?.perStage ?? client.perStage,
    serverPresent: !!serverData,
    recalculating: server.isFetching,
    measuredShare: present.length ? Math.round((measured / present.length) * 100) : null,
    serverError: server.isError ? server.error : null,
    retryServer: () => void server.refetch(),
    runningHours: displayRunning,
    effectiveHours: hasCalendarHours ? serverEffective : null,
    // Lido de `server.data` e não de `serverData`: a base de expediente não depende do rascunho —
    // editar um slider não muda a jornada da fábrica, e apagar o rótulo enquanto se arrasta faria
    // o chip piscar sem nada ter mudado.
    calendarBasis: server.data?.calendarBasis ?? null,
  };
}
